import { ValueFormField } from '@sitecore-jss/sitecore-jss-forms';
import { ValueFieldProps, FieldChangeCallback } from '@sitecore-jss/sitecore-jss-react-forms';
import { FieldValidationErrors } from './FieldValidationErrors';
import { getFormFieldAriaProps } from '../Functions/FormFieldAriaProps';
import { StringInputWithEventViewModel } from '../Types';
import { useEffect, useRef, useState } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { CustomEvent } from '@piwikpro/react-piwik-pro';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import styles from '../JssNextForm.module.scss';

const cx = classNames.bind(styles);

const handleOnChange = (
  field: ValueFormField,
  newValue: string,
  callback: FieldChangeCallback,
  errorMessages: string[]
) => {
  callback(field.valueField.name, [newValue], newValue !== '', errorMessages);
};

export const Recaptcha: React.FunctionComponent<ValueFieldProps<StringInputWithEventViewModel>> = (props) => {
  const { errors, onChange, field } = props;
  const [token, setToken] = useState<string>('');
  const [errMsg, setErrMsg] = useState<string[]>([]);
  const [pageName, setPageName] = useState<string>('');
  const sitekey = process.env['NEXT_PUBLIC_HCAPTCHA_SITEKEY'] ?? '';
  const logo = process.env['NEXT_PUBLIC_HCAPTCHA_LOGO'] ?? '';
  const privacy = process.env['NEXT_PUBLIC_HCAPTCHA_PRIVACY'] ?? '';
  const terms = process.env['NEXT_PUBLIC_HCAPTCHA_TERMS'] ?? '';
  const captchaRef = useRef<HCaptcha>(null);
  const { sitecoreContext } = useSitecoreContext();
  const id = 'chkCaptcha';

  useEffect(() => {
    if (!captchaRef.current?.isReady()) {
      if (sitekey == '') {
        logErr('missing-sitekey');
      }
    }
  }, [captchaRef]);

  //Set page name, used for piwik logging
  useEffect(() => {
    if (sitecoreContext && sitecoreContext.route && typeof sitecoreContext.route.name === 'string') {
      setPageName(sitecoreContext.route.name + ' - ' + window.location.href);
    } else {
      setPageName(window.location.href);
    }
  }, [sitecoreContext, sitecoreContext.route?.displayName]);

  //Update token in form data
  useEffect(() => {
    handleOnChange(field, token, onChange, errMsg);

    //Only reset error messages after token has been reset
    if (token == '') {
      setErrMsg([]);
    }
  }, [token]);

  //onload set form field to empty string for verification purposes
  useEffect(() => {
    handleOnChange(field, '', onChange, []);
  }, []);

  //If any hCaptcha errors are returned while a token is present, reset hCaptcha
  useEffect(() => {
    if (errors.length > 0 && token !== '') {
      CustomEvent.trackEvent('hCaptcha', pageName, 'Token Cleared due to Form Error');
      setErrMsg(errors);
      reset();
    }
  }, [errors]);

  //Reset hCaptcha, clear token, and log event in piwik
  const reset = () => {
    if (token != null) {
      CustomEvent.trackEvent('hCaptcha', pageName, 'Reset, Token Cleared');
      captchaRef.current?.resetCaptcha();
      setToken('');
    }
  };

  //Log any errors hCaptcha throws while loading or running
  const logErr = (event: string) => {
    CustomEvent.trackEvent('hCaptcha', pageName, 'Error: ' + event);
  };

  //Log any attempts to use hCaptcha in piwik
  const verify = (tkn: string) => {
    CustomEvent.trackEvent('hCaptcha', pageName, tkn !== '' ? 'Token Generated' : 'No Token Returned');
    setToken(tkn);
  };

  const runCaptcha = () => {
    if (token == '') {
      CustomEvent.trackEvent('hCaptcha', pageName, 'hCaptcha Challenge Executed');
      captchaRef.current?.execute();
    }
  };

  return (
    <div className={cx('captcha-container')}>
      <div id="upmc-captcha" className={cx('custom-captcha')}>
        <div className={cx('chkbox')}>
          <label htmlFor={`${id}`} className={cx('captcha-label')}>
            <input
              type="checkbox"
              id={id}
              name={id}
              checked={token != ''}
              onClick={runCaptcha}
              {...getFormFieldAriaProps(field, errors)}
            />
            <span>I am not a robot</span>
          </label>
        </div>
        <div className={cx('custom-captcha-logo')}>
          <img src={logo} alt="UPMC HealthPlan" />
          <div className={cx('links')}>
            <a href={privacy} target="_blank">
              Privacy
            </a>
            <a href={terms} target="_blank">
              Terms
            </a>
          </div>
        </div>
      </div>
      <HCaptcha
        sitekey={sitekey}
        onExpire={reset}
        onChalExpired={reset}
        onVerify={verify}
        onError={logErr}
        ref={captchaRef}
        size="invisible"
      />
      <FieldValidationErrors {...props} />
    </div>
  );
};
